/**
 * Authentication utility functions
 */

/**
 * Get authentication headers for API requests
 * @returns {Object} Headers with Authorization if a token exists
 */
export const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    };
    
    const token = localStorage.getItem('token');
    
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
};

/**
 * Get the current user ID from localStorage
 * @returns {number|null} The user ID or null if not logged in
 */
export const getCurrentUserId = (): number | null => {
    try {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return null;
        
        const user = JSON.parse(storedUser);
        return user.userId ? Number(user.userId) : null;
    } catch (error) {
        return null;
    }
};

/**
 * Check if the current user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = (): boolean => {
    return !!localStorage.getItem('token') && !!getCurrentUserId();
};

/**
 * Check if the user is logged in
 * @returns {boolean} True if user is logged in
 */
export const isLoggedIn = (): boolean => {
    return !!localStorage.getItem('token') && !!localStorage.getItem('user');
};

/**
 * Enhanced fetch with authentication headers
 * @param url API endpoint URL
 * @param options fetch options
 * @returns Promise with fetch result
 */
export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers = getAuthHeaders();
    
    // Merge provided headers with auth headers
    const mergedOptions = {
        ...options,
        credentials: 'include' as RequestCredentials, // Include cookies for authentication
        headers: {
            ...headers,
            ...(options.headers || {})
        }
    };
    
    return fetch(url, mergedOptions);
}; 